import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, Edit, School } from '@mui/icons-material';
import { FC, useState } from 'react';

import { useAuth } from '@carcareers/hooks';
import { useJobseekerDeleteEducationMutation, useJobseekerEducationsQuery } from '@carcareers/store';
import { JobseekerEducationModal } from '@carcareers/components';
import { JobseekerEducation } from '@libs/models';

export const EducationCard: FC = () => {
  const { session } = useAuth();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editEducation, setEditEducation] = useState<JobseekerEducation | null>(null);

  const [deleteEducation, { isLoading: isDeleting }] = useJobseekerDeleteEducationMutation();
  const { data: educations } = useJobseekerEducationsQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const handleOpenAdd = () => setIsAddOpen(true);
  const handleCloseAdd = () => setIsAddOpen(false);

  const handleEditEducation = (education: JobseekerEducation) => {
    setEditEducation(education);
  };

  const handleCloseEdit = () => {
    setEditEducation(null);
  };

  const handleDeleteEducation = (educationId: number) => {
    const jobseekerId = session?.profile?.id ?? 0;
    deleteEducation({ jobseekerId, educationId });
  };

  function formatMonthYear(date?: Date | null): string {
    if (!date) return '';
    const newDate = new Date(date);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', timeZone: 'UTC' };
    return new Intl.DateTimeFormat('en-US', options).format(newDate);
  }

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
              <School sx={{ mr: 1 }} />
              Education
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'flex' },
              }}
              onClick={handleOpenAdd}
            >
              Add Education
            </Button>
          </Box>

          <List
            sx={{
              '& .MuiListItemSecondaryAction-root': {
                position: 'unset',
                top: '32px',
                transform: 'unset',
              },
            }}
          >
            {educations?.map((education, index) => (
              <Box key={education.id}>
                <ListItem
                  sx={{ px: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditEducation(education)}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleDeleteEducation(education.id)}
                      >
                        {isDeleting ? <CircularProgress size={18} color="secondary" /> : <Delete sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`${education.level} in ${education.areaOfStudy}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="primary">
                          {education.school}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMonthYear(education.startDate)} -{' '}
                          {education.current ? 'Present' : formatMonthYear(education.endDate)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '1.5em',
                              background: 'linear-gradient(transparent, white)',
                              pointerEvents: 'none',
                            },
                          }}
                        >
                          {education.description}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (educations?.length ?? 0) - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            sx={{
              borderRadius: 2,
              height: 32,
              width: '100%',
              mt: 2,
              display: { xs: 'flex', md: 'none' },
            }}
            onClick={handleOpenAdd}
          >
            Add Education
          </Button>
        </CardContent>
      </Card>

      <JobseekerEducationModal type="add" open={isAddOpen} onClose={handleCloseAdd} />

      {editEducation && (
        <JobseekerEducationModal
          type="edit"
          open={!!editEducation}
          onClose={handleCloseEdit}
          education={editEducation}
        />
      )}
    </>
  );
}; 